import React, { useState } from 'react';
import {
  EmptyFormRow,
  FieldInput,
  FieldTextArea,
  FormCol,
  FormRow,
  useIsFieldRequiredHook,
} from 'motion-components';
import { useFormikContext } from 'formik';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { isActiveField, isPubliclyVisibleInMap } from 'shared-pages/common/form-helpfunc';
import {
  FieldSelectGroup,
  FieldSelectNomos,
  FieldSelectProfession,
  FieldSelectRegion,
  FieldSelectSubregion,
  FieldSelectTaxoffice,
  FormFieldCollectionPointDlg,
  RowFieldTooltips,
} from 'shared-pages/components';
import { Col, Row } from 'react-bootstrap';
import {
  Company,
  CompanyVatTitle,
  isRebatteryLiion,
  isRebatteryLiionApp,
} from 'shared-pages/common/constants';
import {
  isRebattery,
  isFotokiklosi,
  isAfisCyprus,
  isMedicycle,
} from 'shared-pages/common/constants';
import { FieldSelectCpCategory } from 'shared-pages/components/field-select/fs-cpCategory';
import { FieldSelectCpWasteCategory } from 'shared-pages/components/field-select/fs-cpWasteCategory';
import FsCategoryGroup from 'shared-pages/components/field-select/fs-categorygroup';
import { GeocodingFormFields } from './collectionpoint-geocoding-fields';
import { PostalCodeAutocomplete } from '../../../components/field-select/postal-code-autocomplete';
import { gqlGetByIdQuery } from '../graphql';

function applyParentCollectionPointValues(frmkCntx: any, parentData: any) {
  if (!parentData?.getCollectionpointById) return;
  const parent = parentData.getCollectionpointById;
  frmkCntx.setFieldValue('phone', parent.phone || '');
  frmkCntx.setFieldValue('professionId', parent.professionId || '');
  frmkCntx.setFieldValue('groupId', parent.groupId || '');
  frmkCntx.setFieldValue('companyVat', parent.companyVat || '');
  frmkCntx.setFieldValue('companyTaxOffice', parent.companyTaxOffice || '');
  frmkCntx.setFieldValue('name', parent.name || '');
}

export const FormFields = ({
  isNew,
  updateRecord,
  refetch,
  data,
  validationSchema = undefined,
  canEdit,
  isModal = false, // Χρησιμοποιείται για δήλωση του Σημείου απο τον Χρήστη (του Σημειου στο Login)
  initialValues,
}) => {
  const { appStore } = useRootStore();
  const frmkCntx = useFormikContext();
  const { values } = frmkCntx;
  const isFieldRequired = useIsFieldRequiredHook(validationSchema);

  const { handleGraphqlError } = useHandleError('PiBaseFormFields');
  const { t } = useTranslation();

  const geoLabel = (label) => {
    if (data && data.latitude && data.longitude) {
      return (
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${data.latitude},${data.longitude}`}
          target="_blank"
          rel="noreferrer"
        >
          {label}
        </a>
      );
    } else return label;
  };

  const [catGroupCombo, setCatGroupCombo] = useState('');

  // Lazy query for fetching parent collection point details
  const [getParentCollectionPoint, { loading: parentLoading }] = useLazyQuery(gqlGetByIdQuery, {
    fetchPolicy: 'network-only',
    onError: (error) => handleGraphqlError('FormFields', error),
  });

  // Handler for parent collection point selection
  const handleParentCpChange = async (parentCpId, parentCpName) => {
    if (parentCpId && parentCpId !== 'unknown' && !isRebattery) {
      // Fetch parent collection point details when parentCpId changes
      const response = await getParentCollectionPoint({
        variables: { id: parseInt(parentCpId) },
      });
      applyParentCollectionPointValues(frmkCntx, response?.data);
    }
  };

  return (
    <>
      <div className="d-flex flex-row">
        <FormCol>
          <FieldInput
            name="name"
            required={isFieldRequired('name', values)}
            label={t('Επωνυμία')}
            placeholder=""
            disabled={!canEdit}
          />
          {isFotokiklosi && (
            <FieldInput
              required={false}
              label={t('Περιγραφή Διεύθυνσης')}
              name="addressDescription"
              placeholder=""
              disabled={!canEdit}
            />
          )}
          <FieldInput
            name="addressLine1"
            required={isFieldRequired('addressLine1', values)}
            label={t('Διεύθυνση')}
            placeholder=""
            disabled={!canEdit}
          />
          <FieldInput
            label={t('Διεύθυνση - 2η γραμμή')}
            name="addressLine2"
            placeholder=""
            disabled={!canEdit}
          />
          {isFotokiklosi ? (
            <PostalCodeAutocomplete
              name="postalCode"
              label={t('T.K. :')}
              disabled={!canEdit}
              initialData={data}
            />
          ) : (
            <FieldInput label={t('T.K.')} name="postalCode" placeholder="" disabled={!canEdit} />
          )}

          <FieldInput label={t('Περιοχή')} name="addressArea" placeholder="" disabled={!canEdit} />
          <FieldInput label={t('Πόλη')} name="addressCity" placeholder="" disabled={!canEdit} />
          {/* <FieldInput label={t('Νομός')} name="addressNomos" placeholder="" disabled={!canEdit} /> */}
          {!(isAfisCyprus || isMedicycle) && (
            <Row>
              <FieldSelectNomos
                as={Col}
                name={'addressNomos'}
                defaultValues={{ data }}
                required={isFieldRequired('addressNomos', values)}
                disabled={!canEdit}
              />
            </Row>
          )}
        </FormCol>

        {/* 2η Στήλη */}
        <FormCol>
          {!isRebattery && (
            <FieldInput label={t('Κωδικός')} name="code" placeholder="" disabled={!canEdit} />
          )}
          {isFotokiklosi && (
            <FieldSelectProfession name={'professionId'} defaultValues={{ data }} />
          )}
          <FieldInput
            label={t('Υπεύθυνος')}
            name="contactPerson"
            placeholder=""
            disabled={!canEdit}
          />
          <FieldInput
            name="phone"
            required={isFieldRequired('phone', values)}
            label={t('Τηλέφωνο')}
            placeholder=""
            disabled={!canEdit}
          />
          <FieldInput label={t('Κινητό')} name="mobile" placeholder="" disabled={!canEdit} />
          <FieldInput label={t('Φαξ')} name="fax" placeholder="" disabled={!canEdit} />
          <FieldInput
            name="email"
            required={isFieldRequired('email', values)}
            label={t('E-mail')}
            placeholder=""
            disabled={!canEdit}
          />

          <FieldSelectCpCategory
            name="categoryId"
            defaultValues={data}
            disabled={!canEdit || !appStore.isPlatformOwner}
          />

          <Row>
            <FieldSelectRegion
              as={Col}
              name={'regionId'}
              defaultValues={{ data }}
              disabled={!canEdit}
            />
          </Row>
        </FormCol>

        {/* 3η Στήλη */}
        <FormCol>
          <FieldInput
            label={t(CompanyVatTitle)}
            name="companyVat"
            placeholder=""
            disabled={!canEdit}
            required={isFieldRequired('companyVat', values)}
          />
          {!isAfisCyprus &&
            !isMedicycle &&
            (isFotokiklosi ? (
              <FieldSelectTaxoffice
                disabled={!canEdit}
                required={isFieldRequired('companyTaxOffice', values)}
                // as={FormCol}
              />
            ) : (
              <FieldInput
                label={t('Δ.Ο.Υ.')}
                name="companyTaxOffice"
                placeholder=""
                disabled={!canEdit}
                required={isFieldRequired('companyTaxOffice', values)}
              />
            ))}
          <FieldInput
            label={t('Α.Μ. ΗΜΑ')}
            name="imaCode"
            placeholder=""
            disabled={!canEdit}
            required={isFieldRequired('imaCode', values)}
          />
          {isRebatteryLiion && (
            <FieldInput
              label={t('Α.Μ. Συνεργείου στο Μητρώο του Υ.Μ.Ε')}
              name="ymeCode"
              placeholder=""
              disabled={!canEdit}
              required={isFieldRequired('ymeCode', values)}
            />
          )}
          {isFotokiklosi && <FieldSelectGroup />}
          {/* Τριπλό Group */}
          {(isAfisCyprus || isMedicycle) && (
            <Row className="mb-3">
              <FsCategoryGroup
                // reset={reset}
                //   setReset={setReset}
                data={data}
                values={values}
                setFieldValue={frmkCntx.setFieldValue}
                setFieldTouched={frmkCntx.setFieldTouched}
                initialText={catGroupCombo}
                setInitialText={setCatGroupCombo}
              />
            </Row>
          )}
          {!(isRebattery || isRebatteryLiion) && !isModal && (
            <FormFieldCollectionPointDlg
              label={t('Μητρική Εταιρία')}
              idFieldName={'parentCpId'}
              nameFieldName={'parentCollectionpoint.name'}
              disabled={!canEdit}
              onChange={handleParentCpChange}
              isParent={true}
            />
          )}
          {isFotokiklosi ? (
            <FieldInput
              label={t('Κωδικός ERP')}
              name="legacyAppId"
              placeholder=""
              disabled={!canEdit}
            />
          ) : (
            <EmptyFormRow />
          )}
          {isRebatteryLiionApp ? (
            <FieldSelectCpWasteCategory name="wasteCategoryIds" disabled={!canEdit} />
          ) : (
            <EmptyFormRow />
          )}

          <FormRow className="mt-2">
            <FieldSelectSubregion
              as={Col}
              name={'subregionId'}
              defaultValues={{ data }}
              disabled={!canEdit}
              required={isFieldRequired('subregionId', values)}
            />
          </FormRow>
        </FormCol>

        {/* 4η Στήλη */}

        <FormCol>
          {isActiveField(t, canEdit)}
          <EmptyFormRow />
          <FieldTextArea name="notes" label={t('Σχόλια')} rows={6} disabled={!canEdit} />
          {/* <EmptyFormRow /> */}
          {/* <EmptyFormRow /> */}
          <EmptyFormRow />
          <EmptyFormRow />
          <EmptyFormRow />
          <EmptyFormRow />
          {/* {appStore.isPlatformOwner && <RowFieldTooltips data={data} />} */}
        </FormCol>
      </div>
      {/* GeoCoding */}
      {!isModal && <GeocodingFormFields isNew={isNew} data={data} canEdit={canEdit} />}
    </>
  );
};
